import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { MenuItemGroup } from '@/models/metadata';
import { Menu, MenuItemLookupResult, RecentOrFavoriteEntree, RecentOrFavoriteOrder } from '@/models';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { swiper, swiperSlide } from 'vue-awesome-swiper';
import { SwiperOptions } from 'swiper';
import moment from 'moment';
import { formatOrderDate, getCountryCodeByTopLevelDomain } from '@/helpers';

export interface RecentOneTapEntree {
  entree: RecentOrFavoriteEntree;
  orderDate: string;
}

@Component({
  name: 'CmgMealBuilderOneTapReorder',
  components: {
    swiper,
    swiperSlide,
  },
})
export default class CmgMealBuilderOneTapReorder extends Vue {
  @Prop({ required: true, type: Object })
  group!: MenuItemGroup;

  @Prop({ required: true, type: Object })
  menu!: Menu;

  swiperOptions: SwiperOptions = {
    slidesPerView: 'auto',
    centeredSlides: false,
    spaceBetween: 10,
  };

  swiperOptionsDesktop: SwiperOptions = {
    slidesPerView: 3,
    centeredSlides: false,
    spaceBetween: 29,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    },
    nested: false,
  }

  get chevronLeftUrl(): string {
    return this.$appStore.state.cms.images.icons.chevronLeftPlain.default;
  }

  get swiperButtonStyle() {
    return {
      backgroundImage: `url(${this.chevronLeftUrl}) !important`
    }; 
  }

  get showSwiperNavArrows(): boolean {
    if (this.recentEntreesForMealType) {
      return this.recentEntreesForMealType?.length > 3;
    }
    return false;
  }

  get recentOrders(): RecentOrFavoriteOrder[]  {
    return this.$appStore.getters[CUSTOMER_GETTERS.RECENT_ORDERS];
  }

  get menuItemType(): string | undefined {
    return this.group.menuItemType;
  }
  
  get menuItemLookup(): Map<string, MenuItemLookupResult> | null {
    return this.$appStore.state.menu.menuItemLookupMap;
  }

  get recentEntreesForMealType(): RecentOneTapEntree[] | undefined {
    let recentEntrees: RecentOneTapEntree[] = [];

    // Menu item type wont be defined on sides/drinks or top level kids menu
    // We do not want to show one tap reorder cards on these pages
    if (this.menuItemType) {
      this.recentOrders?.forEach(order => {
        order.order.meals?.forEach(meal => {
          meal.entrees?.forEach(entree => {
            if (recentEntrees.length == 5) {
              return;
            } 

            if (this.menuItemLookup?.get(entree.menuItemId)?.menuItemType == this.menuItemType) {
              if (this.isUniqueEntree(entree, recentEntrees) && this.isValidEntree(entree) && !meal.preconfiguredMealId) {
                recentEntrees?.push({ entree, orderDate: formatOrderDate(order.orderDateTime, getCountryCodeByTopLevelDomain()) });
              }
            }
          });
        });
      });
    }
    
    return recentEntrees;
  }

  get showRecentOrdersForMealType(): boolean {
    return !!this.recentEntreesForMealType && !!this.recentEntreesForMealType.length && !!this.$services.customer.isLoggedIn();
  }

  isUniqueEntree(entree: RecentOrFavoriteEntree, recentEntrees: RecentOneTapEntree[]): boolean {
    const duplicateEntree = recentEntrees.find(oneTapEntree => oneTapEntree.entree.menuItemId == entree.menuItemId);
    if (!duplicateEntree) {
      return true;
    }

    // Check if each entrees have the same amount of contents
    if (duplicateEntree.entree.contents && entree.contents && duplicateEntree.entree.contents?.length != entree.contents.length) {
      return true;
    }
    
    // if a duplicate content item is not found, entree is unique
    entree.contents?.forEach(contentItem => {
      if (!duplicateEntree.entree.contents?.find(duplicateContentItem =>  duplicateContentItem.menuItemId == contentItem.menuItemId && duplicateContentItem.customizationId == contentItem.customizationId)) {
        return true;
      }
    });

    return false;
  }

  isValidEntree(entree: RecentOrFavoriteEntree): boolean {
    //Check if current menu has the entree
    if (!this.menuItemLookup?.has(entree.menuItemId)){
      return false;
    }
    //Returns false if there is at least one content not found in the current menu
    return !entree.contents?.some((c=>(!this.menuItemLookup?.has(c.menuItemId)) || false))
  }
}
